import React, { useCallback, useContext, useEffect, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { Alert, Button, Spinner } from "reactstrap";

import { toast } from "react-toastify";

import { ProductContext } from "../../../store";

import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";

import SchemaOptimizerProcessing from "../../../components/AiVisibility/SchemaOptimizer/SchemaOptimizerProcessing";

import SchemaOptimizerResultView from "../../../components/AiVisibility/SchemaOptimizer/SchemaOptimizerResultView";

import {

	getSchemaOptimizerJob,

	isSchemaOptimizerJobFailed,

	isSchemaOptimizerJobInFlight,

	isSchemaOptimizerJobSuccess,

	regenerateSchemaOptimizerJob,

	SCHEMA_OPTIMIZER_JOB_POLL_MS,

} from "../../../services/schemaOptimizerApi";



const AiVisibilitySchemaOptimizerView = () => {

	const navigate = useNavigate();

	const [searchParams, setSearchParams] = useSearchParams();

	const jobId = searchParams.get("jobId");

	const { currentWebsiteId, userLoader, websiteLoader } = useContext(ProductContext);

	const [job, setJob] = useState(null);

	const [loading, setLoading] = useState(true);

	const [regenerating, setRegenerating] = useState(false);



	const load = useCallback(async () => {

		if (!currentWebsiteId || !jobId) {

			setJob(null);

			setLoading(false);

			return;

		}

		const res = await getSchemaOptimizerJob(currentWebsiteId, jobId);

		if (!res.ok) {

			setJob(null);

		} else {

			setJob(res.job);

		}

		setLoading(false);

	}, [currentWebsiteId, jobId]);



	useEffect(() => {

		setLoading(true);

		load();

	}, [load]);



	useEffect(() => {

		if (!job || !currentWebsiteId) return undefined;

		const needsPoll =

			isSchemaOptimizerJobInFlight(job.status) ||

			(isSchemaOptimizerJobSuccess(job.status) && !job.result);

		if (!needsPoll) return undefined;

		const timer = setInterval(load, SCHEMA_OPTIMIZER_JOB_POLL_MS);

		return () => clearInterval(timer);

	}, [job, currentWebsiteId, load]);



	const backTo =

		job?.runId != null || job?.run_id != null

			? `/aivisibility/schema-optimizer/run?runId=${encodeURIComponent(job.runId ?? job.run_id)}`

			: "/aivisibility/schema-optimizer";



	async function handleRegenerate() {

		if (!jobId || regenerating) return;

		setRegenerating(true);

		const res = await regenerateSchemaOptimizerJob(currentWebsiteId, jobId);

		setRegenerating(false);

		if (!res.ok) {

			toast.error(res.message || "Could not regenerate");

			return;

		}

		setSearchParams({ jobId: String(res.job.jobId) });

		setLoading(true);

		load();

	}



	if (userLoader || websiteLoader) return <DetailSkeleton />;



	if (!jobId) {

		return (

			<div className="contentBlock schema-optimizer">

				<p className="text-muted">No job selected.</p>

				<Button color="primary" onClick={() => navigate("/aivisibility/schema-optimizer")}>

					Back to Schema Optimizer

				</Button>

			</div>

		);

	}



	if (loading && !job) {

		return (

			<div className="contentBlock schema-optimizer text-center py-5">

				<Spinner />

			</div>

		);

	}



	if (!job) {

		return (

			<div className="contentBlock schema-optimizer">

				<p className="text-danger">Could not load this job.</p>

				<Button color="link" className="p-0" onClick={() => navigate("/aivisibility/schema-optimizer")}>

					Back

				</Button>

			</div>

		);

	}



	const inFlight = isSchemaOptimizerJobInFlight(job.status);

	const failed = isSchemaOptimizerJobFailed(job.status);

	const success = isSchemaOptimizerJobSuccess(job.status);

	const useBatch = job.useBatchApi ?? job.use_batch_api;

	return (

		<div className="contentBlock ai-visibility-dashboard schema-optimizer schema-optimizer-view">

			<div className="schema-optimizer__session-header schema-optimizer__session-header--compact">

				<button

					type="button"

					className="schema-optimizer__back-btn"

					onClick={() => navigate(backTo)}

				>

					<i className="fas fa-arrow-left me-2" aria-hidden />

					Back

				</button>

			</div>



			{inFlight && (

				<SchemaOptimizerProcessing

					status={job.status}

					progressMessage={job.progressMessage ?? job.progress_message}

					useBatchApi={useBatch}

					pageUrl={job.url}

				/>

			)}



			{failed && job.error && (

				<Alert color="danger" className="mt-3">

					{job.error}

				</Alert>

			)}



			{success && job.result && (

				<SchemaOptimizerResultView
					key={jobId}
					job={job}
					websiteId={currentWebsiteId}
					currentJobId={job.id ?? job.job_id}
					onRegenerate={handleRegenerate}
					regenerating={regenerating || inFlight}
					onSelectJobId={(nextId) => {
						setLoading(true);
						setSearchParams({ jobId: String(nextId) });
					}}
				/>

			)}



			{success && !job.result && (

				<div className="text-center py-4 text-muted">

					<Spinner size="sm" className="me-2" />

					Loading schema comparison…

				</div>

			)}

		</div>

	);

};



export default AiVisibilitySchemaOptimizerView;

